import { Fragment, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, Gavel, ScrollText } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import {
  useDecideDrivingCase,
  useGetDrivingCase,
  useLinkRepairToMissedService,
  useRecordDrivingCaseNotice,
  useRecordDrivingCaseResponse,
  useRecordDrivingHearing,
} from '../../hooks/backend/drivers'
import { GrievanceGuardBanner } from '../grievances/GrievanceGuardBanner'
import { writeErrorMessage } from '../../utils/writeError'
import { readableDate } from '../../utils/coachingDates'
import {
  CATEGORY_LABEL,
  EXCLUSIONS,
  KIND_LABEL,
  NOTICE_METHODS,
  STATUS_LABEL,
  STEP_LABEL,
  dayLabel,
  sastStamp,
  seconds,
  speedLine,
  stepTone,
  type CaseCategory,
  type CaseStatus,
  type DrivingEvent,
  type Step,
} from '../../utils/drivingConduct'

// One driving conduct case, and the only place its steps are recorded.
//
// THE ORDER IS THE DATABASE'S. Notice, then response, then decision; each
// function refuses a step out of order and says what is missing, and that
// sentence is shown as it comes. The form below only offers the step the case is
// on, so nobody is invited to try the wrong one.
//
// THE CHOICES ARE BOUNDED BY THE LADDER. The decision list offers nothing
// heavier than the step the ladder gives, and never dismissal: where the ladder
// gives dismissal the heaviest choice is a hearing, which creates a task for the
// Operational Servant. The database checks all of this again.

type CaseDetail = {
  case_id: string
  driver_name: string | null
  manager_name: string | null
  category: CaseCategory
  offence: string | null
  clause: string | null
  period_start: string
  period_end: string
  allegation: string
  status: CaseStatus
  step: Step | null
  step_label: string
  next_action: string | null
  notice_on: string | null
  notice_method: string | null
  response_due_on: string | null
  response_window_closed: boolean
  responded_on: string | null
  response_text: string | null
  no_response: boolean | null
  exclusion_claimed: string | null
  exclusion_claim_note: string | null
  outcome: string | null
  outcome_reasons: string | null
  evidence: DrivingEvent[]
  history: { at: string; type: string; detail: string; by: string | null }[]
  record: { sanction: string; issued_at: string; valid_until: string | null }[]
  repair_cases: { case_ref: string; status: string }[]
  can_act: boolean
  can_record_hearing: boolean
  can_refer_to_hearing: boolean
}

const RANK: Record<string, number> = { verbal_warning: 1, final_written_warning: 3, termination: 4 }

function todayIso(): string {
  return new Date(Date.now() + 2 * 3600 * 1000).toISOString().slice(0, 10)
}

export function DrivingCaseDialog({
  caseId,
  onClose,
  onChanged,
}: {
  caseId: string | null
  onClose: () => void
  onChanged: () => void
}) {
  const detail = useGetDrivingCase<CaseDetail>()
  const notice = useRecordDrivingCaseNotice()
  const response = useRecordDrivingCaseResponse()
  const decide = useDecideDrivingCase()
  const hearing = useRecordDrivingHearing()
  const link = useLinkRepairToMissedService()

  const [noticeOn, setNoticeOn] = useState(todayIso())
  const [method, setMethod] = useState('')
  const [noticeNote, setNoticeNote] = useState('')
  const [respondedOn, setRespondedOn] = useState(todayIso())
  const [responseText, setResponseText] = useState('')
  const [exclusion, setExclusion] = useState('')
  const [exclusionNote, setExclusionNote] = useState('')
  const [decision, setDecision] = useState('')
  const [reasons, setReasons] = useState('')
  const [upheld, setUpheld] = useState<Set<string>>(new Set())
  const [heldOn, setHeldOn] = useState(todayIso())
  const [hearingOutcome, setHearingOutcome] = useState('')
  const [repairRef, setRepairRef] = useState('')
  const [busy, setBusy] = useState(false)

  const load = detail.trigger
  useEffect(() => {
    if (!caseId) return
    load({ case_id: caseId }, { skipCache: true })
    setMethod('')
    setNoticeNote('')
    setResponseText('')
    setExclusion('')
    setExclusionNote('')
    setDecision('')
    setReasons('')
    setUpheld(new Set())
    setHearingOutcome('')
    setRepairRef('')
  }, [caseId, load])

  const c = caseId && detail.data?.case_id === caseId ? detail.data : null

  const decisions = useMemo(() => {
    if (!c) return []
    const rank = RANK[c.step ?? ''] ?? 0
    const out: { value: string; label: string }[] = [
      { value: 'no_action', label: 'No action' },
      { value: 'withdrawn', label: 'Withdraw the case' },
    ]
    if (rank >= 1) out.push({ value: 'verbal_warning', label: 'Verbal warning' })
    if (rank >= 3) out.push({ value: 'final_written_warning', label: 'Final written warning' })
    if (c.can_refer_to_hearing) out.push({ value: 'refer_to_hearing', label: 'Refer to a hearing' })
    return out
  }, [c])

  async function run(what: string, fn: () => Promise<unknown>) {
    setBusy(true)
    try {
      await fn()
      toast.success(what)
      if (caseId) await load({ case_id: caseId }, { skipCache: true })
      onChanged()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const evidenceCanBeExcluded = c?.status === 'response_received' && !!c.exclusion_claimed

  return (
    <Dialog open={!!caseId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <ScrollText className="h-4 w-4" />
            {c ? `${c.driver_name ?? 'Driver'}: ${CATEGORY_LABEL[c.category]}` : 'Driving conduct case'}
          </DialogTitle>
          <DialogDescription>
            {c
              ? `${c.offence ?? ''}${c.clause ? ` (clause ${c.clause})` : ''}`
              : 'Loading the case.'}
          </DialogDescription>
        </DialogHeader>

        {!c && detail.loading && (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}
        {detail.error && !c && (
          <p className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800">{detail.error}</p>
        )}

        {c && (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Chip tone={c.status === 'decided' || c.status === 'withdrawn' ? 'green' : 'brand'}>
                {STATUS_LABEL[c.status]}
              </Chip>
              <Chip tone={stepTone(c.step)}>The ladder gives: {c.step ? STEP_LABEL[c.step] : 'no step'}</Chip>
              {c.manager_name && <span className="text-xs text-muted-foreground">Driver Servant: {c.manager_name}</span>}
            </div>

            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
              {c.allegation}
            </p>

            {c.next_action && (
              <p className="text-sm font-medium text-foreground">Next: {c.next_action}</p>
            )}

            <div>
              <h4 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">The evidence</h4>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted text-left text-muted-foreground">
                    <tr>
                      {evidenceCanBeExcluded && <th className="px-2 py-1.5">Uphold claim</th>}
                      <th className="px-2 py-1.5">When</th>
                      <th className="px-2 py-1.5">What</th>
                      <th className="px-2 py-1.5">Where</th>
                      <th className="px-2 py-1.5">Speed</th>
                      <th className="px-2 py-1.5">For</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.evidence.map((e, i) => (
                      <tr key={e.event_id ?? i} className="border-t border-border">
                        {evidenceCanBeExcluded && (
                          <td className="px-2 py-1.5">
                            {e.event_id && (
                              <input
                                type="checkbox"
                                aria-label="Uphold the driver's claim for this event"
                                checked={upheld.has(e.event_id)}
                                onChange={(ev) => {
                                  const next = new Set(upheld)
                                  if (ev.target.checked) next.add(e.event_id)
                                  else next.delete(e.event_id)
                                  setUpheld(next)
                                }}
                              />
                            )}
                          </td>
                        )}
                        <td className="whitespace-nowrap px-2 py-1.5">
                          {dayLabel(e.event_date)} {String(e.event_time ?? '').slice(0, 5)}
                        </td>
                        <td className="px-2 py-1.5">
                          {KIND_LABEL[e.kind] ?? e.kind}
                          {e.excluded && <span className="ml-1 text-emerald-700">(excluded)</span>}
                        </td>
                        <td className="px-2 py-1.5">{e.place || e.instruction || '—'}</td>
                        <td className="whitespace-nowrap px-2 py-1.5">{speedLine(e)}</td>
                        <td className="whitespace-nowrap px-2 py-1.5">{seconds(e.duration_s)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {(c.notice_on || c.responded_on || c.no_response || c.outcome) && (
              <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
                {c.notice_on && (
                  <>
                    <dt className="text-muted-foreground">Written notice</dt>
                    <dd>
                      {readableDate(c.notice_on)} by {NOTICE_METHODS.find((m) => m.value === c.notice_method)?.label ?? c.notice_method}
                      {c.response_due_on && `, response due by ${readableDate(c.response_due_on)}`}
                    </dd>
                  </>
                )}
                {(c.responded_on || c.no_response) && (
                  <>
                    <dt className="text-muted-foreground">Driver's response</dt>
                    <dd>{c.no_response ? 'None by the due date' : `${readableDate(c.responded_on)}: ${c.response_text}`}</dd>
                  </>
                )}
                {c.exclusion_claimed && (
                  <>
                    <dt className="text-muted-foreground">Exclusion claimed</dt>
                    <dd>
                      {EXCLUSIONS.find((x) => x.value === c.exclusion_claimed)?.label}: {c.exclusion_claim_note}
                    </dd>
                  </>
                )}
                {c.outcome && (
                  <>
                    <dt className="text-muted-foreground">Decision</dt>
                    <dd>
                      {c.outcome.replace(/_/g, ' ')}: {c.outcome_reasons}
                    </dd>
                  </>
                )}
                {c.record.map((r) => (
                  <Fragment key={`${r.sanction}-${r.issued_at}`}>
                    <dt className="text-muted-foreground">On the record</dt>
                    <dd>
                      {r.sanction.replace(/_/g, ' ')} from {readableDate(r.issued_at)}
                      {r.valid_until ? `, lapses ${readableDate(r.valid_until)}` : ''}
                    </dd>
                  </Fragment>
                ))}
              </dl>
            )}

            {!c.can_act && c.status !== 'decided' && c.status !== 'withdrawn' && (
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                This driver is not in your book. Their own Driver Servant records the steps on this case, or an
                administrator can.
              </p>
            )}

            {c.can_act && c.status === 'raised' && (
              <fieldset className="space-y-2 rounded-md border border-border p-3" disabled={busy}>
                <legend className="px-1 text-xs font-semibold">1. The written notice you gave the driver</legend>
                <p className="text-xs text-muted-foreground">
                  Nothing is sent from here. Give the driver the notice yourself, in writing, with the evidence above,
                  then record when and how.
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="dc-notice-on">Date given</Label>
                    <Input id="dc-notice-on" type="date" value={noticeOn} onChange={(e) => setNoticeOn(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="dc-method">How</Label>
                    <NativeSelect id="dc-method" value={method} onChange={(e) => setMethod(e.target.value)}>
                      <option value="">Choose</option>
                      {NOTICE_METHODS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                </div>
                <Textarea placeholder="Anything worth noting (optional)" value={noticeNote} onChange={(e) => setNoticeNote(e.target.value)} />
                <Button
                  size="sm"
                  onClick={() =>
                    run('Notice recorded', () =>
                      notice.trigger({ case_id: c.case_id, notice_on: noticeOn, method, note: noticeNote || null }),
                    )
                  }
                >
                  Record the notice
                </Button>
              </fieldset>
            )}

            {c.can_act && c.status === 'notice_issued' && (
              <fieldset className="space-y-2 rounded-md border border-border p-3" disabled={busy}>
                <legend className="px-1 text-xs font-semibold">2. The driver's response</legend>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="dc-responded">Date they responded</Label>
                    <Input id="dc-responded" type="date" value={respondedOn} onChange={(e) => setRespondedOn(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="dc-exclusion">Exclusion claimed</Label>
                    <NativeSelect id="dc-exclusion" value={exclusion} onChange={(e) => setExclusion(e.target.value)}>
                      <option value="">None claimed</option>
                      {EXCLUSIONS.map((x) => (
                        <option key={x.value} value={x.value}>
                          {x.label}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                </div>
                <Textarea placeholder="What the driver said, in their words as far as you can" value={responseText} onChange={(e) => setResponseText(e.target.value)} />
                {exclusion && (
                  <Textarea
                    placeholder="What the driver offered for the exclusion: which clip, what happened on the road"
                    value={exclusionNote}
                    onChange={(e) => setExclusionNote(e.target.value)}
                  />
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      run('Response recorded', () =>
                        response.trigger({
                          case_id: c.case_id,
                          responded_on: respondedOn,
                          response: responseText,
                          exclusion_claimed: exclusion || null,
                          exclusion_note: exclusionNote || null,
                        }),
                      )
                    }
                  >
                    Record the response
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!c.response_window_closed}
                    title={c.response_window_closed ? undefined : `The driver has until the end of ${readableDate(c.response_due_on)}`}
                    onClick={() =>
                      run('Recorded that no response came', () =>
                        response.trigger({ case_id: c.case_id, no_response: true }),
                      )
                    }
                  >
                    No response came
                  </Button>
                </div>
              </fieldset>
            )}

            {((c.can_act && c.status === 'response_received') || c.can_record_hearing) && (
              // Clauses 31.14 and 27.8: an open or recent grievance from this
              // driver is checked before a decision against him. The trigger on
              // conduct_cases (db/migrations/0723) enforces it; this explains it.
              <GrievanceGuardBanner caseKind="conduct_case" caseId={c.case_id} />
            )}

            {c.can_act && c.status === 'response_received' && (
              <fieldset className="space-y-2 rounded-md border border-border p-3" disabled={busy}>
                <legend className="px-1 text-xs font-semibold">3. Your decision</legend>
                {evidenceCanBeExcluded && (
                  <p className="text-xs text-muted-foreground">
                    Tick the events the driver showed were necessary. They stop counting, and if what is left is no
                    longer a breach, only no action or withdrawal can be recorded.
                  </p>
                )}
                <NativeSelect value={decision} onChange={(e) => setDecision(e.target.value)} aria-label="Decision">
                  <option value="">Choose a decision</option>
                  {decisions.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </NativeSelect>
                <Textarea placeholder="Your reasons, as you would give them to the driver" value={reasons} onChange={(e) => setReasons(e.target.value)} />
                <Button
                  size="sm"
                  onClick={() =>
                    run(decision === 'refer_to_hearing' ? 'Referred to a hearing' : 'Decision recorded', () =>
                      decide.trigger({
                        case_id: c.case_id,
                        decision,
                        reasons,
                        upheld_event_ids: Array.from(upheld),
                      }),
                    )
                  }
                >
                  {decision === 'refer_to_hearing' ? (
                    <>
                      <Gavel className="mr-1 h-3.5 w-3.5" /> Refer to a hearing
                    </>
                  ) : (
                    'Record the decision'
                  )}
                </Button>
              </fieldset>
            )}

            {c.can_record_hearing && (
              <fieldset className="space-y-2 rounded-md border border-border p-3" disabled={busy}>
                <legend className="px-1 text-xs font-semibold">The hearing's outcome</legend>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="dc-held">Held on</Label>
                    <Input id="dc-held" type="date" value={heldOn} onChange={(e) => setHeldOn(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="dc-outcome">Outcome</Label>
                    <NativeSelect id="dc-outcome" value={hearingOutcome} onChange={(e) => setHearingOutcome(e.target.value)}>
                      <option value="">Choose</option>
                      <option value="no_action">No action</option>
                      <option value="verbal_warning">Verbal warning</option>
                      <option value="final_written_warning">Final written warning</option>
                      <option value="termination">Termination</option>
                    </NativeSelect>
                  </div>
                </div>
                <Textarea placeholder="The hearing's reasons" value={reasons} onChange={(e) => setReasons(e.target.value)} />
                <Button
                  size="sm"
                  onClick={() =>
                    run('Hearing outcome recorded', () =>
                      hearing.trigger({ case_id: c.case_id, held_on: heldOn, outcome: hearingOutcome, reasons }),
                    )
                  }
                >
                  Record the outcome
                </Button>
              </fieldset>
            )}

            {c.category === 'missed_designated_service' && c.status === 'decided' && c.can_act && (
              <fieldset className="space-y-2 rounded-md border border-border p-3" disabled={busy}>
                <legend className="px-1 text-xs font-semibold">A repair that followed this missed service</legend>
                <p className="text-xs text-muted-foreground">
                  Linking marks the repair as a missed service loss. Nothing is recovered until every section 34
                  condition is recorded on the repair case, as for any at-fault damage.
                </p>
                {c.repair_cases.map((r) => (
                  <p key={r.case_ref} className="flex items-center gap-1 text-xs">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> {r.case_ref} ({r.status})
                  </p>
                ))}
                <div className="flex gap-2">
                  <Input placeholder="Repair case reference" value={repairRef} onChange={(e) => setRepairRef(e.target.value)} />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      run('Repair case linked', () => link.trigger({ case_ref: repairRef, conduct_case_id: c.case_id }))
                    }
                  >
                    Link
                  </Button>
                </div>
              </fieldset>
            )}

            <div>
              <h4 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">History</h4>
              <ul className="space-y-1 text-xs">
                {c.history.map((h, i) => (
                  <li key={i}>
                    <span className="text-muted-foreground">{sastStamp(h.at)}</span>
                    {h.by ? ` ${h.by}:` : ''} {h.detail}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
